import React, { useRef, useState, memo, useEffect } from "react";
import { useDrag, useDrop } from "react-dnd";
import { PlusIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { budgetInputEditText, formatBudgetAmount, formatBudgetInputAmount } from "../../helpers/quickbooks";
import { getEmptyImage } from "react-dnd-html5-backend";
import { getDisplayedBudgetLineTotal, getDisplayedBudgetLineValue } from "./budgetActualsDisplay";

const ItemTypes = { ROW: "BUDGET_ROW" };

const amountTextClass = (value, positiveClass = "text-gray-900") =>
  Number(String(value ?? "").replace(/[$,\s]/g, "")) < 0 ? "text-red-600" : positiveClass;

// Helper function to normalize numeric values (handles thousands separators, currency symbols, etc.)
const normalizeNumericValue = (value) => {
  if (!value || value === '') return null;
  
  // Remove currency symbols, spaces, and other non-numeric characters except digits, comma, dot, and minus
  let cleanedValue = value.replace(/[$\s]/g, '');
  
  // Handle thousands separators and decimal separators
  let normalizedValue = cleanedValue;
  
  // Check if comma is a thousands separator
  // Pattern: digits followed by comma and exactly 3 digits, optionally repeated, then dot and more digits or end
  // Examples: "15,290.00", "1,234,567.89", "15,290"
  const thousandsPattern = /^-?\d{1,3}(?:,\d{3})+(\.\d+)?$/;
  const hasThousandsSeparator = thousandsPattern.test(cleanedValue);
  
  if (hasThousandsSeparator) {
    // Remove thousands separators (commas)
    normalizedValue = cleanedValue.replace(/,/g, '');
  } else {
    // Check if it's European format (comma as decimal separator)
    // Pattern: digits,comma,1-2digits at end (no dot present)
    const europeanPattern = /^-?\d+,\d{1,2}$/;
    if (europeanPattern.test(cleanedValue) && !cleanedValue.includes('.')) {
      // Replace comma with dot for decimal separator
      normalizedValue = cleanedValue.replace(',', '.');
    } else if (cleanedValue.includes(',')) {
      // If there's a comma but doesn't match patterns above, remove it
      normalizedValue = cleanedValue.replace(/,/g, '');
    }
  }
  
  // Validate the normalized value
  if (!/^-?\d*\.?\d*$/.test(normalizedValue)) {
    return null;
  }
  
  // Clean up leading zeros
  if (
    normalizedValue.includes('.') &&
    normalizedValue.startsWith('0') &&
    !normalizedValue.startsWith('0.') &&
    !normalizedValue.startsWith('-0.')
  ) {
    normalizedValue = normalizedValue.replace(/^0+/, '') || '0';
  } else if (!normalizedValue.includes('.') && normalizedValue.length > 1) {
    normalizedValue = normalizedValue.replace(/^(-?)0+/, '$1') || '0';
  }
  
  return normalizedValue;
};

const DraggableBudgetRowComponent = ({
  budgetLineItem,
  index,
  access,
  categoryId,
  moveRow,
  resetLocalOrdering,
  persistOrderIfChanged,
  inputIsLoading,
  onEditFormulaClick,
  onEditLineItemClick,
  onDeleteClick,
  rollupCandidates,
  sourceAccount,
  sourceAccounts = [],
  itemInputValues,
  handleInputChange,
  handleInputBlur,
  closedMonths,
  actualsByMonth = [],
  demandPlanLinked = false,
  demandPlanName = 'Demand Plan',
}) => {
  const rowRef = useRef(null);
  const handleRef = useRef(null);
  const [isEditingCell, setIsEditingCell] = useState(false);
  const [focusedMonthKey, setFocusedMonthKey] = useState(null);

  const [{ isDragging }, drag, preview] = useDrag({
    type: ItemTypes.ROW,
    canDrag: () => access === "edit" && !isEditingCell,
    item: {
      index,
      id: budgetLineItem?.id,
      sourceCategoryId: categoryId,
      selectedType: budgetLineItem?.type,
      selectedForecastValue: budgetLineItem?.forecastValue,
      selectedRollUpItems: budgetLineItem?.rollupItems || [],
    },
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  });

  const [{ isOver, clientOffset, canDrop }, drop] = useDrop({
    accept: ItemTypes.ROW,
    // Only allow reordering within the same category (disable cross-category drops)
    canDrop: (dragged) => dragged?.sourceCategoryId === categoryId,
    hover: (dragged, monitor) => {
      if (!rowRef.current) return;
      const dragIndex = dragged.index;
      const hoverIndex = index;
      if (dragIndex === hoverIndex) return;
      if (!isEditingCell && dragged.sourceCategoryId === categoryId) {
        moveRow(dragIndex, hoverIndex);
      }
      // Track intended drop position relative to this row for cross-category moves
      const clientOffset = monitor.getClientOffset();
      const rect = rowRef.current.getBoundingClientRect();
      const midY = rect.top + rect.height / 2;
      const isAbove = clientOffset ? clientOffset.y < midY : true;
      dragged.targetIndex = hoverIndex;
      dragged.targetPosition = isAbove ? 'before' : 'after';
      dragged.index = hoverIndex;
    },
    drop: async (dragged, monitor) => {
      if (!dragged || !monitor.canDrop()) return;
      // Same-category drop only: persist local order
      await persistOrderIfChanged();
      resetLocalOrdering?.();
    },
    collect: (monitor) => ({
      isOver: monitor.isOver({ shallow: true }),
      clientOffset: monitor.getClientOffset(),
      canDrop: monitor.canDrop(),
    }),
  });

  drop(rowRef);
  drag(handleRef);
  // Remove default browser drag preview (screenshot) so we only see our thin line placeholder
  useEffect(() => {
    preview(getEmptyImage(), { captureDraggingState: true });
  }, [preview]);

  const lineItemType = budgetLineItem.type;
  // Compute insert indicator (top/bottom line) when hovered
  let insertBorderTop = false;
  let insertBorderBottom = false;
  if (isOver && canDrop && rowRef.current && clientOffset) {
    const rect = rowRef.current.getBoundingClientRect();
    const midY = rect.top + rect.height / 2;
    const isAbove = clientOffset.y < midY;
    insertBorderTop = isAbove;
    insertBorderBottom = !isAbove;
  }
  // While dragging, keep row height but fade contents; show only insert lines
  const contentHiddenStyle = isDragging ? { opacity: 0 } : {};

  return (
    <tr
      ref={rowRef}
      className={`group border-b border-gray-100 transition-colors ${isDragging ? "" : "hover:bg-gray-50"}`}
      style={{
        opacity: 1,
        borderTop: insertBorderTop ? "2px solid rgba(59,130,246,1)" : undefined,
        borderBottom: insertBorderBottom ? "2px solid rgba(59,130,246,1)" : undefined,
      }}
      key={budgetLineItem?.id || `${lineItemType}_${index}`}
    >
      <td
        data-sticky-col="1"
        className="relative w-[104px] min-w-[104px] align-middle bg-white px-2 py-1.5 text-[13px] font-normal tabular-nums text-gray-500 group-hover:bg-gray-50"
        style={{
          left: 0,
          position: "sticky",
          zIndex: 3,
          ...(isDragging ? { boxShadow: "none" } : {}),
          ...contentHiddenStyle,
        }}
      >
        <div className="flex flex-row items-center gap-1 whitespace-nowrap">
          {access === "edit" && (
            <span
              ref={handleRef}
              tabIndex={0}
              aria-label="Drag budget line item"
              className="inline-flex items-center text-gray-400 transition-colors hover:text-gray-600"
            >
              <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </span>
          )}
          <span>{budgetLineItem.accountNumber || "N/A"}</span>
          {budgetLineItem.type === "EDITABLE" && access === "edit" && (
            <button
              className="group/action flex h-4 w-4 items-center justify-center gap-2 rounded-md whitespace-nowrap text-sm font-medium hover:bg-gray-100"
              onClick={(e) => {
                e.stopPropagation();
                onEditFormulaClick?.({
                  budgetLineItemId: budgetLineItem?.id,
                  hasFormula: budgetLineItem?.hasFormula,
                  formula: budgetLineItem?.formula,
                });
              }}
            >
              <PlusIcon className="h-3 w-3 text-gray-400 transition-colors group-hover/action:text-gray-600" />
            </button>
          )}
        </div>
      </td>
      <td
        data-sticky-col="2"
        className="w-[176px] min-w-[176px] align-middle bg-white px-3 py-1.5 text-[13px] font-medium text-gray-700 group-hover:bg-gray-50"
        style={{
          left: "var(--budget-grid-coa-width)",
          position: "sticky",
          zIndex: 2,
          boxShadow: "2px 0 0 0 rgba(0,0,0,0.06)",
          ...(isDragging ? { boxShadow: "none" } : {}),
          ...contentHiddenStyle,
        }}
      >
        <div className="flex flex-row items-center gap-2 whitespace-nowrap">
          {access === "edit" ? (
            <button
              type="button"
              className="cursor-pointer whitespace-nowrap rounded-md text-left text-inherit hover:text-gray-900 hover:underline focus:outline-none focus:ring-2 focus:ring-blue-100"
              title="Edit budget line item"
              onClick={(e) => {
                e.stopPropagation();
                onEditLineItemClick?.({
                  budgetLineItemId: budgetLineItem?.id,
                  type: budgetLineItem?.type,
                  forecastValue: budgetLineItem?.forecastValue,
                  categoryId: budgetLineItem?.categoryId,
                  rollupItems: rollupCandidates,
                  selectedRPItems: budgetLineItem?.rollupItems || [],
                  code: budgetLineItem?.accountNumber,
                  name: budgetLineItem?.accountName,
                });
              }}
            >
              {budgetLineItem.accountName || "N/A"}
            </button>
          ) : (
            <span>{budgetLineItem.accountName || "N/A"}</span>
          )}
          {budgetLineItem.type === "CALCULATED" &&
            budgetLineItem.formula?.is_percentage && (
              <span
                className="shrink-0 whitespace-nowrap rounded bg-gray-100 px-1 py-0.5 text-[11px] font-semibold text-gray-600"
                title={`Percentage of ${sourceAccount?.accountName || sourceAccount?.accountNumber || budgetLineItem.formula?.source_account || "source account"}`}
              >
                % of Rev
              </span>
            )}
          {budgetLineItem.hasFormula && !budgetLineItem.formula?.is_percentage && (
            <span className="text-gray-400">f(x)</span>
          )}
        </div>
      </td>
      {(budgetLineItem.monthsValue || []).map((lineItem, monthIndex) => {
        const monthKey = Object.keys(lineItem)[0];
        const forecastValue = lineItem[monthKey];
        const isClosedMonth = closedMonths?.[monthIndex] === true;
        const actuals = isClosedMonth ? actualsByMonth[monthIndex] : null;
        const hasActuals = Boolean(actuals?.lines);
        const actualsDividerIndex = actualsByMonth.reduce(
          (lastIndex, monthActuals, index) => monthActuals?.lines ? index : lastIndex,
          -1,
        );
        const actualsDivider = monthIndex === actualsDividerIndex ? "border-r-2 border-indigo-200" : "";
        const value = getDisplayedBudgetLineValue(budgetLineItem, monthIndex, actualsByMonth, sourceAccounts);
        const monthInputValues = itemInputValues || {};
        const hasDraft = Object.prototype.hasOwnProperty.call(monthInputValues, monthKey);
        const draft = monthInputValues[monthKey];
        const isDemandPlanGross = demandPlanLinked && ["40100", "41100"].includes(String(budgetLineItem.accountNumber));
        if (hasActuals) {
          const capturedDate = actuals.captured_at
            ? `${String(new Date(actuals.captured_at).getMonth() + 1).padStart(2, '0')}/${String(new Date(actuals.captured_at).getDate()).padStart(2, '0')}/${new Date(actuals.captured_at).getFullYear()}`
            : 'unknown date';
          return (
            <td
              key={monthIndex}
              title={`QuickBooks P&L actual captured ${capturedDate}. Retained forecast value: ${formatBudgetAmount(forecastValue)}.`}
              className={`min-w-[96px] whitespace-nowrap border-indigo-200 bg-indigo-50 px-1 py-1 text-right text-[13px] font-medium tabular-nums ${actualsDivider} ${amountTextClass(value)}`}
              style={contentHiddenStyle}
            >
              {formatBudgetAmount(value)}
            </td>
          );
        }
        if (isDemandPlanGross && !isClosedMonth) {
          return (
            <td
              key={monthIndex}
              title={`From Demand Plan ${demandPlanName}. Change it on the Demand Plan, then Review changes.`}
              className={`min-w-[96px] whitespace-nowrap bg-white px-1 py-1 text-right text-[13px] font-medium tabular-nums ${amountTextClass(forecastValue)}`}
              style={contentHiddenStyle}
            >
              {formatBudgetAmount(forecastValue)}
            </td>
          );
        }
        const inputValue = hasDraft
          ? draft ?? ""
          : focusedMonthKey === monthKey
            ? budgetInputEditText(value)
            : formatBudgetInputAmount(forecastValue);
        return lineItemType === "EDITABLE" && !isClosedMonth ? (
            <td key={monthIndex} className="relative min-w-[96px] align-middle px-1 py-1 text-right text-[13px] font-medium tabular-nums text-gray-900" style={contentHiddenStyle}>
              <div className="flex items-center justify-end gap-1 whitespace-nowrap">
                <span className="text-gray-400">$</span>
                <input
                  className={`h-7 w-full min-w-0 flex-1 rounded-md border border-transparent bg-transparent px-0 py-1 text-right text-[13px] tabular-nums ring-offset-background transition-colors hover:border-gray-300 hover:bg-white focus:border-blue-300 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-100 disabled:bg-gray-200 no-arrows-by-input no-spinner ${amountTextClass(inputValue)}`}
                  type="text"
                  inputMode="decimal"
                  value={inputValue}
                  disabled={inputIsLoading || access !== "edit"}
                  draggable={false}
                  onMouseDown={(e) => e.stopPropagation()}
                  onTouchStart={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                  onKeyUp={(e) => e.stopPropagation()}
                  onFocus={() => {
                    setIsEditingCell(true);
                    setFocusedMonthKey(monthKey);
                  }}
                  onPaste={(e) => {
                    e.preventDefault();
                    const pastedText = (e.clipboardData || window.clipboardData).getData('text');
                    if (access === "edit" && !isClosedMonth && pastedText) {
                      let rawValue = pastedText;

                      // Remove currency symbols, spaces, and other non-numeric characters except digits, comma, dot, and minus
                      let cleanedValue = rawValue.replace(/[$\s]/g, '');

                      // Normalize the value
                      let normalizedValue = normalizeNumericValue(cleanedValue);

                      if (normalizedValue !== null) {
                        handleInputChange(budgetLineItem.id, monthKey, normalizedValue);
                      }
                    }
                  }}
                  onChange={(e) => {
                    if (access === "edit") {
                      let rawValue = e.target.value;

                      // Remove currency symbols, spaces, and other non-numeric characters except digits, comma, dot, and minus
                      let cleanedValue = rawValue.replace(/[$\s]/g, '');

                      // Normalize the value
                      let normalizedValue = normalizeNumericValue(cleanedValue);

                      if (normalizedValue !== null) {
                        handleInputChange(budgetLineItem.id, monthKey, normalizedValue);
                      }
                    }
                  }}
                  onBlur={() => {
                    if (access === "edit") {
                      const currentValue = itemInputValues?.[monthKey];
                      if (
                        Object.prototype.hasOwnProperty.call(itemInputValues || {}, monthKey) &&
                        currentValue != null &&
                        currentValue !== "" &&
                        Number.isFinite(Number(currentValue)) &&
                        Number(currentValue) !== Number(forecastValue)
                      ) {
                        handleInputBlur(budgetLineItem.id, monthKey, currentValue);
                      }
                    }
                    setFocusedMonthKey(null);
                    setIsEditingCell(false);
                  }}
                />
              </div>
            </td>
        ) : (
            <td
              key={monthIndex}
              title={isClosedMonth
                ? (isDemandPlanGross
                  ? `Closed. Retained forecast from Demand Plan ${demandPlanName}; not QuickBooks actuals.`
                  : 'Closed. Retained budget forecast; not QuickBooks actuals.')
                : undefined}
              className={`min-w-[96px] whitespace-nowrap align-middle px-1 py-1 text-right text-[13px] font-medium tabular-nums ${actualsDivider} ${amountTextClass(value)}`}
              style={contentHiddenStyle}
            >
              {formatBudgetAmount(value)}
            </td>
        );
      })}
      <td className={`min-w-[140px] whitespace-nowrap align-middle px-1 py-1 text-right text-[13px] font-semibold tabular-nums ${amountTextClass(getDisplayedBudgetLineTotal(budgetLineItem, actualsByMonth, sourceAccounts))}`} style={contentHiddenStyle}>
        {formatBudgetAmount(getDisplayedBudgetLineTotal(budgetLineItem, actualsByMonth, sourceAccounts))}
      </td>
      {access === "edit" && (
        <td className="align-middle px-3 py-1 text-center" style={contentHiddenStyle}>
          <button
            className="group/delete inline-flex h-6 w-6 items-center justify-center gap-2 whitespace-nowrap p-0 text-sm font-medium opacity-0 ring-offset-background transition-opacity group-hover:opacity-100 focus:opacity-100 hover:bg-gray-100"
            onClick={(e) => {
              e.stopPropagation();
              onDeleteClick?.(budgetLineItem.id);
            }}
          >
            <XMarkIcon className="h-3 w-3 text-gray-400 transition-colors group-hover/delete:text-gray-600" />
          </button>
        </td>
      )}
    </tr>
  );
};

const areEqual = (prevProps, nextProps) => {
  // Compare minimal state to avoid re-renders when parent functions change identity
  if (prevProps.access !== nextProps.access) return false;
  if (prevProps.categoryId !== nextProps.categoryId) return false;
  if (prevProps.actualsByMonth !== nextProps.actualsByMonth) return false;
  const prevClosedMonths = prevProps.closedMonths || [];
  const nextClosedMonths = nextProps.closedMonths || [];
  if (prevClosedMonths.length !== nextClosedMonths.length) return false;
  if (prevClosedMonths.some((isClosed, index) => isClosed !== nextClosedMonths[index])) return false;
  if ((prevProps.budgetLineItem?.id || null) !== (nextProps.budgetLineItem?.id || null)) return false;
  if (prevProps.index !== nextProps.index) return false;
  if (prevProps.inputIsLoading !== nextProps.inputIsLoading) return false;
  // Re-render when row type changes (e.g., CALCULATED <-> EDITABLE)
  if ((prevProps.budgetLineItem?.type || null) !== (nextProps.budgetLineItem?.type || null)) return false;
  // Re-render when formula presence changes (icon/state)
  if ((prevProps.budgetLineItem?.hasFormula || false) !== (nextProps.budgetLineItem?.hasFormula || false)) return false;
  // Re-render when month values or total changed
  const prevMV = prevProps.budgetLineItem?.monthsValue || [];
  const nextMV = nextProps.budgetLineItem?.monthsValue || [];
  if (prevMV.length !== nextMV.length) return false;
  for (let i = 0; i < prevMV.length; i++) {
    const p = prevMV[i] || {};
    const n = nextMV[i] || {};
    const pk = Object.keys(p)[0];
    const nk = Object.keys(n)[0];
    if (pk !== nk) return false;
    if (Number(p[pk] ?? 0) !== Number(n[nk] ?? 0)) return false;
  }
  if (Number(prevProps.budgetLineItem?.total ?? 0) !== Number(nextProps.budgetLineItem?.total ?? 0)) return false;
  // Compare item input values shallow by keys and values
  const a = prevProps.itemInputValues || {};
  const b = nextProps.itemInputValues || {};
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) return false;
  for (let k of aKeys) {
    if (a[k] !== b[k]) return false;
  }
  return true;
};

export const DraggableBudgetRow = memo(DraggableBudgetRowComponent, areEqual);

export default DraggableBudgetRow;
